import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, signIn, resendSignUpCode } from 'aws-amplify/auth';
import '../App.css';

function Signup() {
  const [step,     setStep]     = useState('signup'); // 'signup' | 'verify'
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [role,     setRole]     = useState('client');
  const [code,     setCode]     = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [resent,   setResent]   = useState(false);
  const navigate = useNavigate();

  // ── Password strength ──────────────────────────────────────────────────────
  const pwStrength = (pw) => {
    let s = 0;
    if (pw.length >= 8)          s++;
    if (/[A-Z]/.test(pw))        s++;
    if (/[0-9]/.test(pw))        s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };
  const strength      = pwStrength(password);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', '#dc2626', '#d97706', '#2563eb', 'var(--green)'][strength];

  // ── Sign up ────────────────────────────────────────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await signUp({
        username: email,
        password,
        options: { userAttributes: { name, email, 'custom:role': role } },
      });
      setStep('verify');
    } catch (err) {
      // If this email already signed up but never verified, resend the code
      // and move them to the verify step instead of showing a confusing error
      if (
        err.name === 'UsernameExistsException' ||
        err.message?.includes('already exists') ||
        err.message?.includes('User already exists')
      ) {
        try {
          await resendSignUpCode({ username: email });
          setStep('verify');
        } catch {
          // If resend also fails the account is fully confirmed — tell them to log in
          setError('An account with this email already exists. Please sign in instead.');
        }
        setLoading(false);
        return;
      }
      setError(err.message);
    }
    setLoading(false);
  };

  // ── Verify ─────────────────────────────────────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      await signIn({ username: email, password });
      navigate(role === 'worker' ? '/dashboard' : '/client-dashboard');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // ── Resend code ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    setError(''); setResent(false);
    try {
      await resendSignUpCode({ username: email });
      setResent(true);
      setTimeout(() => setResent(false), 4000);
    } catch (err) {
      setError(err.message);
    }
  };

  const roles = [
    { key: 'client', icon: '🔍', title: "I'm a Client",   sub: 'Browse and book local service providers' },
    { key: 'worker', icon: '✂',  title: "I'm a Provider", sub: 'List services and accept bookings' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Card */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '24px', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>

          {/* Green header */}
          <div style={{ background: 'var(--green)', padding: '2rem 2.5rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, background: 'radial-gradient(circle, rgba(74,158,107,0.4) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Link to="/" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', textDecoration: 'none', display: 'block', marginBottom: '1rem' }}>Vyblo</Link>
              {step === 'signup' && (
                <>
                  <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.25rem' }}>Create your account</h1>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}>Start booking or get booked on Vyblo</p>
                </>
              )}
              {step === 'verify' && (
                <>
                  <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.25rem' }}>Check your email</h1>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}>We sent a code to <strong>{email}</strong></p>
                </>
              )}
            </div>
          </div>

          {/* Form body */}
          <div style={{ padding: '2rem 2.5rem' }}>

            {/* Error */}
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#dc2626', marginBottom: '1.25rem', display: 'flex', gap: '0.5rem' }}>
                <span style={{ flexShrink: 0 }}>⚠️</span>
                <span>
                  {error}
                  {error.includes('sign in instead') && (
                    <> <Link to="/login" style={{ color: '#dc2626', fontWeight: 700 }}>Sign in →</Link></>
                  )}
                </span>
              </div>
            )}

            {/* ── SIGNUP FORM ── */}
            {step === 'signup' && (
              <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {/* Role selector */}
                <div>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.625rem' }}>I am a...</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
                    {roles.map(r => (
                      <button key={r.key} type="button" onClick={() => setRole(r.key)}
                        style={{ padding: '0.875rem', background: role === r.key ? 'var(--green-pale)' : 'var(--cream)', border: `2px solid ${role === r.key ? 'var(--green)' : 'var(--border)'}`, borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', fontFamily: 'DM Sans, sans-serif' }}
                        onMouseEnter={e => { if (role !== r.key) e.currentTarget.style.borderColor = 'rgba(26,92,58,0.3)'; }}
                        onMouseLeave={e => { if (role !== r.key) e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        <div style={{ fontSize: '1.25rem', marginBottom: '0.35rem' }}>{r.icon}</div>
                        <p style={{ fontSize: '0.85rem', fontWeight: 700, color: role === r.key ? 'var(--green)' : 'var(--text)', marginBottom: '0.15rem' }}>{r.title}</p>
                        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{r.sub}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Full Name</label>
                  <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Email</label>
                  <input type="email" placeholder="you@gmail.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" style={{ paddingRight: '2.75rem' }} />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', padding: 0, lineHeight: 1 }}>
                      {showPw ? '🙈' : '👁'}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.3rem' }}>
                        {[1,2,3,4].map(i => (
                          <div key={i} style={{ flex: 1, height: '4px', borderRadius: '99px', background: i <= strength ? strengthColor : 'var(--cream-mid)', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      <p style={{ fontSize: '0.72rem', color: strengthColor, fontWeight: 600 }}>{strengthLabel}</p>
                    </div>
                  )}
                </div>

                {role === 'worker' && (
                  <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.78rem', color: 'var(--text-mid)', lineHeight: 1.55 }}>
                    🚀 As a provider you'll set up your services, availability, and connect your bank account to start accepting bookings.
                  </div>
                )}

                <button type="submit" className="btn-primary btn-full" disabled={loading} style={{ marginTop: '0.25rem' }}>
                  {loading ? 'Creating account...' : 'Create Account →'}
                </button>
              </form>
            )}

            {/* ── VERIFY FORM ── */}
            {step === 'verify' && (
              <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', borderRadius: '10px', padding: '0.875rem 1rem', fontSize: '0.82rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>
                  📧 Check your inbox for a 6-digit code from Vyblo. It may take a minute — check your spam folder too.
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Verification Code</label>
                  <input type="text" placeholder="123456" value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required maxLength={6}
                    style={{ letterSpacing: '0.25em', fontSize: '1.25rem', textAlign: 'center', fontWeight: 700 }}
                  />
                </div>
                <button type="submit" className="btn-primary btn-full" disabled={loading || code.length < 6}>
                  {loading ? 'Verifying...' : 'Verify & Continue →'}
                </button>

                {/* Resend code */}
                <div style={{ textAlign: 'center' }}>
                  {resent ? (
                    <p style={{ fontSize: '0.82rem', color: 'var(--green)', fontWeight: 600 }}>✓ Code resent! Check your inbox.</p>
                  ) : (
                    <button type="button" onClick={handleResend}
                      style={{ background: 'transparent', border: 'none', color: 'var(--green)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: '0.25rem 0' }}>
                      Didn't get a code? Resend →
                    </button>
                  )}
                </div>

                <button type="button" onClick={() => { setStep('signup'); setCode(''); setError(''); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: '0.25rem 0' }}>
                  ← Wrong email? Go back
                </button>
              </form>
            )}
          </div>
        </div>

        {step === 'signup' && (
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>Sign in</Link>
          </p>
        )}

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: 'var(--green)' }}>Vyblo</div>
          <p style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: '0.2rem' }}>Book local services you love</p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
